import { Base } from '@studiometa/js-toolkit';
import ProductLookCard from './ProductLookCard.js';
import ProductShopLookSlider from './ProductShopLookSlider.js';

/**
 * ProductShopLook - Manages selection of multiple products in a look
 */
export default class ProductShopLook extends Base {
  /**
   * Loading state
   */
  isLoading = false;

  /**
   * Component Configuration
   */
  static config = {
    name: 'ProductShopLook',
    refs: ['submitButton', 'finalPrice', 'spinner'],
    components: {
      ProductLookCard,
      ProductShopLookSlider,
    },
  };

  /**
   * Mounted hook
   */
  mounted() {
    // Update submit button state and total price based on initial selection
    this.updateButtonState();
    this.updateTotalPrice();
  }

  /**
   * Handle selection change from child cards
   */
  onProductLookCardClicked() {
    this.updateButtonState();
    this.updateTotalPrice();
  }

  /**
   * Handle submit button click - Add all selected items to cart
   */
  async onSubmitButtonClick() {
    if (this.isLoading) {
      return;
    }

    const selectedItems = this.selectedCartItems;

    if (selectedItems.length === 0) {
      return;
    }

    this.isLoading = true;
    this.$refs.submitButton.disabled = true;

    if (this.$refs.spinner) {
      this.$refs.spinner.classList.remove('opacity-0');
    }

    try {
      await window.liquidAjaxCart.add({ items: selectedItems });
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      this.isLoading = false;
      this.updateButtonState();

      if (this.$refs.spinner) {
        this.$refs.spinner.classList.add('opacity-0');
      }
    }
  }

  /**
   * Update submit button enabled/disabled state
   */
  updateButtonState() {
    if (this.$refs.submitButton) {
      const hasSelection = this.selectedCartItems.length > 0;
      this.$refs.submitButton.disabled = !hasSelection;
    }
  }

  /**
   * Update total price display
   */
  updateTotalPrice() {
    if (!this.$refs.finalPrice) {
      return;
    }

    const hasSelection = this.selectedCartItems.length > 0;

    this.$refs.finalPrice.classList.toggle('hidden', !hasSelection);

    if (!hasSelection) {
      return;
    }

    this.$refs.finalPrice.textContent = ProductShopLook.formatPrice(this.totalPrice);
  }

  /**
   * Format an amount in the visitor's presentment currency.
   *
   * Le format n'est pas codé en dur : il est déduit des gabarits rendus par
   * `format-price.liquid` (cf. `shopify-global-vars.liquid`), pour que ce total
   * calculé côté client s'affiche exactement comme les prix rendus en Liquid.
   * @param {number} amount Amount in cents, in the presentment currency
   * @returns {string} Formatted price, currency symbol included
   */
  static formatPrice(amount) {
    const { sample = '', sampleRound = '' } = window.currencyFormat ?? {};
    const format = ProductShopLook.parseCurrencyFormat(sample, sampleRound);

    if (!format) {
      return String(amount / 100);
    }

    const { prefix, suffix, group, decimal, hideZeroCents } = format;
    const showCents = Boolean(decimal) && !(hideZeroCents && amount % 100 === 0);
    const [integer, cents] = (amount / 100).toFixed(showCents ? 2 : 0).split('.');
    const grouped = group ? integer.replaceAll(/\B(?=(\d{3})+(?!\d))/g, group) : integer;

    return `${prefix}${grouped}${cents ? decimal + cents : ''}${suffix}`;
  }

  /**
   * Derive the currency format from the sentinel prices rendered by Liquid.
   * @param {string} sample Le montant 1 111 222,33 formaté par Shopify
   * @param {string} sampleRound Le montant 1 111 222,00 formaté par Shopify
   * @returns {object | null} Les composants du format, ou null si inexploitable
   */
  static parseCurrencyFormat(sample, sampleRound) {
    const firstDigit = sample.search(/\d/);

    if (firstDigit === -1) {
      return null;
    }

    // Index juste après le dernier chiffre, pour isoler un éventuel suffixe.
    const afterLastDigit =
      sample.length - [...sample].reverse().findIndex((char) => /\d/.test(char));
    const core = sample.slice(firstDigit, afterLastDigit);
    const suffix = sample.slice(afterLastDigit);

    // Les décimales sentinelles valent 33 : ce qui les précède est le
    // séparateur décimal. Absentes, la devise n'a pas de sous-unité (JPY…).
    const tail = core.slice(-3);
    const decimal = /^\D33$/.test(tail) ? tail[0] : '';

    // La partie entière fait 7 chiffres : un séparateur de milliers tombe donc
    // forcément en deuxième position.
    const group = /\d/.test(core[1]) ? '' : core[1];

    return {
      prefix: sample.slice(0, firstDigit),
      suffix,
      group,
      decimal,
      hideZeroCents: Boolean(decimal) && !sampleRound.endsWith(`${decimal}00${suffix}`),
    };
  }

  /**
   * Get total price of selected products
   * @returns {number} Total price in cents
   */
  get totalPrice() {
    return this.$children.ProductLookCard.filter((card) => card.isSelected).reduce(
      (total, card) => total + card.price,
      0,
    );
  }

  /**
   * Get selected cart items from child components
   * @returns {Array} Array of cart items {id, quantity}
   */
  get selectedCartItems() {
    return this.$children.ProductLookCard.filter((card) => card.isSelected)
      .map((card) => card.cartItem)
      .filter((item) => item !== null);
  }
}
